import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Task } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';
import { Brain } from 'lucide-react';
import { toast } from 'sonner';

import { useCreateAudioGenerationWithDataset } from '@/api/generated';
import DatasetCombobox from '@/components/dataset-combobox';
import ModelsCombobox from '@/components/models-combobox';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import {
  CreateAudioGenerationWithDatasetSchema,
  createAudioGenerationWithDatasetSchema,
} from '@/routes/(app)/(tasks)/lib/generate-audio-schema';

const GenerateAudioWithDataset = () => {
  const { mutate, isPending } = useCreateAudioGenerationWithDataset();

  const form = useForm<CreateAudioGenerationWithDatasetSchema>({
    defaultValues: {
      models: [],
    },
    resolver: zodResolver(createAudioGenerationWithDatasetSchema),
  });
  const { t } = useTranslation();

  const router = useRouter();

  const handleSubmit = useCallback(
    (values: CreateAudioGenerationWithDatasetSchema) => {
      mutate(
        {
          data: {
            datasetId: values.datasetId,

            models: values.models,
          },
        },
        {
          onSuccess: async () => {
            toast.success(t('clusters.createModelRun.success'));
            await router.navigate({
              to: '/app/clusters',
              search: { datasetId: values.datasetId, isEval: true },
            });
          },
          onError: () => {
            toast.error(t('clusters.createModelRun.error'));
          },
        }
      );
    },
    [mutate, router, t]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle className='text-2xl'>
              {t('tasks.generateAudio.form.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='datasetId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.generateAudio.form.dataset.label')}
                  </FormLabel>
                  <FormControl>
                    <DatasetCombobox
                      task={Task.GENERATE_AUDIO}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='models'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.generateAudio.form.models.label')}
                  </FormLabel>
                  <ModelsCombobox
                    task={Task.GENERATE_AUDIO}
                    values={field.value}
                    {...field}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type='submit'
              disabled={isPending}>
              <Button.Icon side={'left'}>
                {isPending ? <Spinner className='text-inherit' /> : <Brain />}
              </Button.Icon>
              {t('tasks.generateAudio.form.submit')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};

export default GenerateAudioWithDataset;
